import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  ArrowLeft,
  FileText,
  ExternalLink,
  Calendar,
  Upload,
  CheckCircle,
  AlertCircle,
  Users,
  Plus,
  X
} from "lucide-react";
import { toast } from "sonner";

export default function TrainingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [training, setTraining] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(null);
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    description: "",
    campaignId: "",
    requiresAcknowledgement: false
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [trainingRes, campaignsRes] = await Promise.all([
        apiClient.get("/training"),
        apiClient.get("/campaigns")
      ]);
      setTraining(trainingRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error("Failed to load training materials");
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (trainingId) => {
    setAcknowledging(trainingId);
    try {
      await apiClient.post(`/training/${trainingId}/acknowledge`);
      const trainingRes = await apiClient.get("/training");
      setTraining(trainingRes.data);
      toast.success("Training acknowledged");
    } catch (error) {
      toast.error("Failed to acknowledge training");
    } finally {
      setAcknowledging(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Only PDF files are allowed");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadTraining = async () => {
    if (!uploadForm.title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!selectedFile) {
      toast.error("Please select a PDF file");
      return;
    }

    setUploading(true);
    try {
      // Step 1: Upload the PDF file
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const uploadRes = await apiClient.post("/training/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      // Step 2: Create the training record with the uploaded file URL
      await apiClient.post("/training", {
        title: uploadForm.title.trim(),
        description: uploadForm.description.trim() || "",
        pdfUrl: uploadRes.data.url,
        campaignId: uploadForm.campaignId || null,
        requiresAcknowledgement: uploadForm.requiresAcknowledgement
      });

      toast.success("Training material uploaded successfully!");
      
      // Reset form and close modal
      setShowUploadModal(false);
      setUploadForm({
        title: "",
        description: "",
        campaignId: "",
        requiresAcknowledgement: false
      });
      setSelectedFile(null);
      
      // Reload data
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload training material");
    } finally {
      setUploading(false);
    }
  };

  const filteredTraining = selectedCampaign === "all" 
    ? training 
    : training.filter(t => !t.campaignId || t.campaignId === selectedCampaign);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading training...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="training-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-10 px-3"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="font-heading font-black text-xl text-slate-900">Training</div>
            <div className="text-sm text-slate-500">Campaign resources & guides</div>
          </div>
          {user?.role === "owner" && (
            <Button
              size="sm"
              className="h-10 bg-emerald-500 hover:bg-emerald-600"
              onClick={() => setShowUploadModal(true)}
              data-testid="upload-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Training
            </Button>
          )}
        </div>
      </header>

      {/* Main */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Filter */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">Filter by campaign:</span>
          <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
            <SelectTrigger className="w-48 h-10" data-testid="campaign-filter">
              <SelectValue placeholder="All campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Training List */}
        {filteredTraining.length > 0 ? (
          <div className="space-y-3" data-testid="training-list">
            {filteredTraining.map((item) => (
              <Card key={item.id} className="border-slate-200 shadow-none">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-bold text-slate-900">
                          {item.title}
                        </h3>
                        {item.requiresAcknowledgement && (
                          item.isAcknowledged ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-amber-500" />
                          )
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mb-2 line-clamp-2">
                        {item.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(item.createdAt)}
                        </span>
                        {item.campaignId && (
                          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                            {campaigns.find(c => c.id === item.campaignId)?.name || "Campaign"}
                          </span>
                        )}
                        {user?.role === "owner" && item.requiresAcknowledgement && (
                          <span className="flex items-center gap-1 text-slate-500">
                            <Users className="h-3 w-3" />
                            {item.acknowledgedCount}/{item.totalUsers} acknowledged
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9"
                        onClick={() => window.open(item.pdfUrl, '_blank')}
                        data-testid={`view-training-${item.id}`}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {item.requiresAcknowledgement && !item.isAcknowledged && user?.role === "fundraiser" && (
                        <Button
                          size="sm"
                          className="h-9 bg-emerald-500 hover:bg-emerald-600"
                          onClick={() => handleAcknowledge(item.id)}
                          disabled={acknowledging === item.id}
                          data-testid={`acknowledge-training-${item.id}`}
                        >
                          {acknowledging === item.id ? (
                            <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Acknowledge
                            </>
                          )}
                        </Button>
                      )}
                      {item.requiresAcknowledgement && item.isAcknowledged && user?.role === "fundraiser" && (
                        <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Acknowledged
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No training materials available</p>
            <p className="text-sm text-slate-400 mt-1">
              {selectedCampaign !== "all" ? "Try selecting a different campaign" : "Check back later for updates"}
            </p>
          </div>
        )}
      </main>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-emerald-600" />
              Upload Training Material
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 font-medium">Title *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="e.g., Pitch Script Guide"
                className="mt-1.5"
                data-testid="training-title-input"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 font-medium">Description</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of this training material..."
                className="mt-1.5 min-h-[80px]"
                data-testid="training-description-input"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 font-medium">Campaign</Label>
              <Select 
                value={uploadForm.campaignId || "all"} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, campaignId: v === "all" ? "" : v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="training-campaign-select">
                  <SelectValue placeholder="All campaigns (org-wide)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campaigns (org-wide)</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-700 font-medium">PDF File *</Label>
              <div className="mt-1.5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="training-file-input"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:border-slate-300 transition-colors"
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-8 w-8 text-red-500" />
                      <div className="text-left">
                        <div className="font-medium text-slate-900">{selectedFile.name}</div>
                        <div className="text-xs text-slate-500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 ml-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <div className="text-slate-600 font-medium">Click to select PDF</div>
                      <div className="text-xs text-slate-400 mt-1">or drag and drop</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <div className="font-medium text-slate-900">Requires Acknowledgement</div>
                <div className="text-sm text-slate-500">Fundraisers must confirm they've read this</div>
              </div>
              <Switch
                checked={uploadForm.requiresAcknowledgement}
                onCheckedChange={(checked) => setUploadForm({ ...uploadForm, requiresAcknowledgement: checked })}
                data-testid="training-acknowledgement-switch"
              />
            </div>
            
            <Button
              onClick={handleUploadTraining}
              disabled={uploading || !uploadForm.title.trim() || !selectedFile}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600"
              data-testid="submit-training-btn"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload Training
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
